import SpaceBetween from '@risk-smart/themed-cloudscape-components/space-between';
import Button from '@risksmart-app/components/src/button';
import { PageNotFound } from '@risksmart-app/components/src/errors/errors';
import { useGetGuidParam } from '@risksmart-app/components/src/routes/routes.utils';
import { ParentTypes as Parent_Type_Enum } from '@risksmart-app/domain/src/types/consts/parent-type';
import type { FC } from 'react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import type { ObjectToolbarConfig } from 'src/blocks/object-toolbar';
import { ObjectToolbar } from 'src/blocks/object-toolbar';
import { useObjectToolbarFormStore } from 'src/blocks/object-toolbar/useObjectToolbarFormStore';
import ControlledTabs from 'src/components/controlled-tabs';
import DeleteModal from 'src/components/delete-modal/DeleteModal';
import TabSettingsModal from 'src/components/tab-settings-modal';
import { PageLayout } from 'src/layouts';
import { Permission } from 'src/rbac/Permission';
import {
  useGetDetailParentPath,
  useGetDetailPath,
} from 'src/routes/useGetDetailParentPath';

import { useDeleteActions } from '@/hooks/mutations/action';
import { useGetActionById } from '@/hooks/queries';
import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { useDeleteResultNotification } from '@/hooks/useMutationResultNotification';
import useTabs from '@/hooks/useTabs';
import { getFriendlyId } from '@/utils/friendlyId';

import useExporter from './useExporter';

type Props = {
  activeTabId: 'details' | 'linkedItems' | 'notificationHistory' | 'updates';
  showDeleteButton?: boolean;
};

const Page: FC<Props> = ({ activeTabId, showDeleteButton }) => {
  const { t: st } = useTranslation(['common'], { keyPrefix: 'actions' });
  const { t } = useTranslation(['common']);
  const defaultTitle = st('fallback_title');
  const objectToolbarEnabled = useIsFeatureFlagEnabled('dynamic_forms');
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [showTabSettings, setShowTabSettings] = useState(false);
  // Form-editor opener bridged up from the active tab's FormMenu (relocate mode).
  // Present only on the details tab when dynamic_forms are on.
  const formConfig = useObjectToolbarFormStore((s) => s.formConfigState);
  const navigate = useNavigate();
  const actionId = useGetGuidParam('actionId');
  const [exportItem, { loading: exporting }] = useExporter(actionId);
  const parentPath = useGetDetailParentPath(actionId);
  const detailsPath = useGetDetailPath(actionId);

  const { data, error } = useGetActionById({ queryArgs: { id: actionId } });
  if (error) {
    throw error;
  }

  const { deleteActions, loading: deleteLoading } = useDeleteActions();

  const action = data?.action?.[0];
  const tabs = useTabs({
    parentType: Parent_Type_Enum.Action,
    parent: action,
    hrefRoot: detailsPath,
  });

  if (data?.action.length === 0) {
    throw new PageNotFound(`Action with id ${actionId} not found`);
  }

  const onDelete = useDeleteResultNotification({
    entityName: st('entity_name'),
    asyncAction: async () => {
      if (!action) {
        return false;
      }
      await deleteActions({ Ids: [action.Id] });
      navigate(parentPath);

      return true;
    },
  });

  const title = action?.Title || defaultTitle;
  const friendlyId =
    action && getFriendlyId(Parent_Type_Enum.Action, action.SequentialId);
  const counter = friendlyId && `(${friendlyId})`;

  // Object-level toolbar (CUS-154) — flag-gated. Relocates Export + Delete into
  // the toolbar's overflow ⋮ menu and surfaces the Save disc / Cancel X (save
  // state bridged from the active tab's form via useObjectToolbarFormStore).
  // Delete tracks `showDeleteButton` — the toolbar's danger slot only supports a
  // permission gate, so the route-level boolean (delete shows on the details tab
  // only) is applied by conditionally including the danger action (parity).
  const toolbarConfig: ObjectToolbarConfig = useMemo(
    () => ({
      header: {
        // `||` (not `??`) to match the legacy `title` fallback — an empty-string
        // Title must fall back to defaultTitle for flag on/off parity.
        title: action?.Title || defaultTitle,
        // Pass the already-parenthesised counter (the toolbar renders it verbatim).
        counter: counter || undefined,
      },
      parentObject: action,
      save: true,
      actions: {
        overflow: [
          {
            id: 'edit',
            label: t('edit'),
            actions: [
              ...(formConfig
                ? [
                    {
                      id: 'edit-form',
                      label: t('edit_form'),
                      iconName: 'file-06' as const,
                      onClick: formConfig.openConfigure,
                    },
                  ]
                : []),
              {
                id: 'edit-tabs',
                label: t('edit_tabs'),
                iconName: 'layers-three-01',
                onClick: () => setShowTabSettings(true),
              },
            ],
          },
        ],
        export: [
          {
            id: 'export',
            actions: [
              {
                id: 'export',
                label: t('export.standard_pdf'),
                iconName: 'download-01',
                onClick: exportItem,
                disabled: exporting,
              },
            ],
          },
        ],
        danger: showDeleteButton
          ? [
              {
                id: 'delete',
                label: st('delete_button'),
                iconName: 'trash-01',
                permission: 'delete:action',
                // Same hydration gate as the legacy Delete button below — see the
                // comment there. Both entry points open the same modal, so both
                // have to wait for `action` or the delete fires with no id.
                disabled: !action,
                onClick: () => setIsDeleteModalVisible(true),
              },
            ]
          : undefined,
      },
    }),
    [
      action,
      defaultTitle,
      counter,
      t,
      st,
      exportItem,
      exporting,
      formConfig,
      showDeleteButton,
    ]
  );

  return (
    <PageLayout
      title={title}
      meta={{
        title: defaultTitle,
      }}
      counter={counter}
      headerOverride={
        objectToolbarEnabled ? (
          <ObjectToolbar config={toolbarConfig} />
        ) : undefined
      }
      actions={
        <SpaceBetween direction={'horizontal'} size={'xs'}>
          <Button
            iconName={'download'}
            disabled={exporting}
            onClick={exportItem}
          >
            {t('export.export')}
          </Button>
          {showDeleteButton && (
            <Permission permission={'delete:action'} parentObject={action}>
              <Button
                variant={'normal'}
                formAction={'none'}
                // Disabled until the entity has hydrated. The page renders (and
                // this button with it) while `useGetActionById` is still in
                // flight, so a click landing in that window reached `onDelete`
                // with `action === undefined`, took the `return false` guard, and
                // surfaced as a bare "Failed to save changes" with NO request
                // ever issued — the delete looked broken when it had simply been
                // fired too early (ENG-616). V3 widens the window because the
                // read routes tRPC → data-layer → perm_v2, which is why it
                // hard-failed there and only flaked on legacy.
                //
                // This also restores Playwright's actionability wait: a disabled
                // button makes `click()` wait for hydration instead of racing it,
                // so no spec needs its own guard.
                disabled={!action}
                onClick={() => {
                  setIsDeleteModalVisible(true);
                }}
              >
                {st('delete_button')}
              </Button>
            </Permission>
          )}
        </SpaceBetween>
      }
    >
      <ControlledTabs
        activeTabId={activeTabId}
        tabs={tabs}
        variant={'container'}
        parentType={Parent_Type_Enum.Action}
        parent={action}
        disableSettings={objectToolbarEnabled}
      />
      <TabSettingsModal
        parentType={Parent_Type_Enum.Action}
        isVisible={showTabSettings}
        onDismiss={() => setShowTabSettings(false)}
        parent={action}
      />
      <DeleteModal
        loading={deleteLoading}
        isVisible={isDeleteModalVisible}
        header={t('delete')}
        onDelete={onDelete}
        onDismiss={() => setIsDeleteModalVisible(false)}
      >
        {st('confirm_delete_message')}
      </DeleteModal>
    </PageLayout>
  );
};

export default Page;
